import React, { useEffect, useState, memo } from 'react';
import axios from 'axios';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useForm, useField } from 'react-final-form';
import { GalleryItem } from '../../components/GalleryItem';
import CONFIG from '../../../../../config';
import {
  FORM_VALUES,
  MAX_PHOTOS_LENGTH
} from '../../../../../utils/helpers/constants';
import { FormBlockWrapper } from '../FormBlockWrapper';
import { FormErrorField } from '../../components/FormErrorField';
import { Image, PhotosProps } from './types';
import { UploadPhotoButton } from '../../components/UploadPhotoButton';
import { ValidationCounterText } from '../styled';
import { PhotosOuterWrapper, PhotosWrapper } from './styled';
import useScreenSize from '../../../../../hooks/useScreenSize';
import { AlertBanner } from '../../components/AlertBanner';

const errorHandler = error => {
  console.error(
    'POND API Error:',
    error.response ? error.response.data : error.message
  );
};

const PhotosComponent: React.FC<PhotosProps> = ({ loading, setLoading }) => {
  const { change } = useForm();
  const { t, i18n } = useTranslation();
  const { input: imagesField } = useField(FORM_VALUES.images, {
    subscription: { value: true }
  });
  const { input: categoryValuesField } = useField(FORM_VALUES.categoryValues, {
    subscription: { value: true }
  });
  const { isSmallOrLargeDesktop } = useScreenSize();

  const [loadingObject, setLoadingObject] = useState({});
  const [images, setImages] = useState<Image[]>(imagesField.value || []);
  const [sourceObject, setSourceObject] = useState({});
  const [uploadsCounter, setUploadsCounter] = useState(0);

  const sensors = useSensors(
    isSmallOrLargeDesktop
      ? null
      : useSensor(TouchSensor, {
          activationConstraint: { delay: 100, tolerance: 5 }
        }),
    !isSmallOrLargeDesktop ? null : useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  useEffect(() => {
    const isLoading = Object.values(loadingObject).some(Boolean);
    setLoading(isLoading);
  }, [loadingObject]);

  useEffect(() => {
    const isMockImages = images.some(el => el.thumbnail_url === null);
    if (images.length && !loading && !isMockImages) {
      change(FORM_VALUES.images, images);
    }
  }, [images, loading]);

  useEffect(() => {
    if (imagesField.value === undefined && images.length) {
      setImages([]);
    }
    if (imagesField.value?.length && !images.length) {
      setImages(imagesField.value);
    }
  }, [imagesField.value]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over?.id && active.id !== over.id) {
      const oldIndex = images.findIndex(el => el.id === active.id);
      const newIndex = images.findIndex(el => el.id === over.id);

      if (oldIndex >= 0 && newIndex >= 0) {
        const newArray = arrayMove(images, oldIndex, newIndex).filter(Boolean);
        setImages(newArray);
      }
    }
  };

  const catchErrors = (i: string) => err => {
    if (!axios.isCancel(err)) {
      // eslint-disable-next-line no-alert
      alert('Could not upload your photo. Please, try again.');
      setImages(prevState => prevState.filter(el => el.id !== i));
      errorHandler(err);
    }
  };

  const handleUpload = async (formData: FormData, i: string) => {
    const source = axios.CancelToken.source();
    setSourceObject(prevState => ({ ...prevState, [i]: source }));
    setLoadingObject(prevState => ({ ...prevState, [i]: true }));
    axios
      .post(`${CONFIG.photosBaseUrl}/pond`, formData, {
        params: { lang: i18n.language },
        cancelToken: source.token,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        setImages(prevState =>
          prevState.map(el => (el.id === i ? response.data.photos[0] : el))
        );
      })
      .catch(catchErrors(i))
      .finally(() =>
        setLoadingObject(prevState => ({ ...prevState, [i]: false }))
      );
  };

  const handleFileInputChange = (files: FileList | null) => {
    const counter = uploadsCounter + 1;
    setUploadsCounter(counter);

    if (files?.length) {
      const newFiles = Array.from(files);
      const initialArr: Image[] = [];

      const newImages = newFiles.reduce((acc, file, i) => {
        const mockImage = {
          id: `${counter}-${i}`,
          thumbnail_url: null
        };

        acc.push(mockImage);
        const formData = new FormData();
        formData.append('images[]', file);
        handleUpload(formData, `${counter}-${i}`);
        return acc;
      }, initialArr);

      setImages(prevState => [...prevState, ...newImages]);
    }
  };

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: string
  ) => {
    e.stopPropagation();

    if (sourceObject[id]) {
      sourceObject[id].cancel('Operation canceled by the user.');
    }
    const updatedList = images?.filter(image => image?.id !== id);
    setImages(updatedList);
  };

  const handleRotate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    params: { id: string; angle: number }
  ) => {
    e.stopPropagation();
    axios
      .post(`${CONFIG.photosBaseUrl}/pond/rotate`, null, {
        params: {
          id: params.id,
          angle: params.angle,
          lang: i18n.language
        },
        headers: {
          Accept: 'application/json'
        }
      })
      .then(response => {
        const newValues = images.map(image =>
          image?.id === params.id ? response.data : image
        );
        setImages(newValues);
      })
      .catch(errorHandler);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const { files } = event.dataTransfer;
    handleFileInputChange(files);
  };

  return (
    <FormBlockWrapper header={t('new_ad.photos')} required>
      <PhotosOuterWrapper>
        <AlertBanner
          title={t('new_ad.photos_restrict_title')}
          data-cy="photos-alert-banner"
        >
          {t('new_ad.photos_restrict_message')}
        </AlertBanner>
        <div>
          <div onDragOver={onDragOver} onDrop={onDrop}>
            <PhotosWrapper data-cy="item-modal-photo-wrapper">
              <DndContext
                sensors={sensors}
                onDragEnd={onDragEnd}
                collisionDetection={closestCenter}
              >
                <SortableContext items={images.map(img => img.id)}>
                  {images.map((_, index) => {
                    const indexWithCounter = `${uploadsCounter}-${index}`;
                    const id = images[index]?.id || indexWithCounter;
                    const url = images[index]?.thumbnail_url;

                    return (
                      <GalleryItem
                        id={id}
                        key={id}
                        url={url}
                        isLoading={loadingObject[id]}
                        handleDelete={e => handleDelete(e, id)}
                        handleRotateLeft={e =>
                          handleRotate(e, { id, angle: -90 })
                        }
                        handleRotateRight={e =>
                          handleRotate(e, { id, angle: 90 })
                        }
                      />
                    );
                  })}

                  {!!images.length && (
                    <UploadPhotoButton
                      onChange={handleFileInputChange}
                      hasImages
                    />
                  )}
                </SortableContext>
              </DndContext>
            </PhotosWrapper>

            {!images.length && (
              <UploadPhotoButton onChange={handleFileInputChange} />
            )}
          </div>

          <ValidationCounterText data-cy="item-modal-validation-counter">
            {t('new_ad.min_max_photos', {
              valueMin: categoryValuesField.value?.photosRequired,
              valueMax: MAX_PHOTOS_LENGTH
            })}
          </ValidationCounterText>

          <FormErrorField
            field={FORM_VALUES.images}
            cypress="images-field-error"
            withPadding={false}
          />
        </div>
      </PhotosOuterWrapper>
    </FormBlockWrapper>
  );
};

export const Photos = memo(PhotosComponent);
